import { APIKit } from "config/axios/index";
import store from "config/redux/index";
import { start, end } from "config/redux/slice";

import { ServerConstants, ServiceConstants } from "constant";
import { ServiceModel } from "models";
import { ClientNewRequest, ListFilterRequest, UserRequest, UserStatus } from "types";

import { handleErrorWithTokenRenewal } from "./common";
import { AppUtils } from "utils";
import { FileWithPath } from "@mantine/dropzone";

const { API_V1_ME, API_V1_USERS, API_V1_USER_RECOMMENDATIONS, API_V1_USER_COMMENTS } =
  ServerConstants;
const {
  REQUEST_USERS_CLIENTS_ID,
  REQUEST_USERS_CLIENTS_MY_ID,
  REQUEST_USERS_CLIENTS_NEW_ID,
  REQUEST_USERS_CLIENTS_STATS_ID,
  REQUEST_USERS_CLIENTS_VIEW_ID,
  REQUEST_USERS_CLIENTS_UPDATE_PROPERTY_ID,
  REQUEST_USERS_CLIENTS_DELETE_ID,
  REQUEST_USERS_CLIENTS_BLACKLIST_ID,
  REQUEST_USERS_LIST_ID,
  REQUEST_USERS_CREATE_ID,
  REQUEST_USERS_UPDATE_ID,
  REQUEST_USER_CHANGE_PASSWORD,
  REQUEST_USER_CHANGE_AVATAR,
  REQUEST_USER_RECOMMENDATION_STATS_ID,
  REQUEST_USER_RECOMMENDATION_LIST_ID,
  REQUEST_USER_RECOMMENDATION_TRANSFER_TO_CRM_ID,
  REQUEST_USER_COMMENTS_CREATE_ID,
  REQUEST_USER_COMMENTS_LIST_ID,
  REQUEST_USER_AGENTS_LIST_ID,
  REQUEST_USER_AGENT_CHANGE_ID,
} = ServiceConstants;

interface UserClientFilterRequest extends ListFilterRequest {
  phase: string;
  source?: string;
  agent?: boolean;
}
export async function userClients(filter: UserClientFilterRequest) {
  const requestId = REQUEST_USERS_CLIENTS_ID;
  try {
    store.dispatch(start(requestId));
    let url = `${API_V1_USERS}/clients?page=${filter.page}`;
    if (filter.search) url += `&search=${filter.search}`;
    if (filter.source) url += `&source=${filter.source.toUpperCase()}`;
    if (filter.phase) url += `&phase=${filter.phase.toUpperCase()}`;
    if (filter.agent) url += `&agent=true`;
    const response = await APIKit.get(url);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, userClients, [filter], requestId);
  }
}

export async function userClientsMy(filter: ListFilterRequest) {
  const requestId = REQUEST_USERS_CLIENTS_MY_ID;
  try {
    store.dispatch(start(requestId));
    let url = `${API_V1_USERS}/clients/my?page=${filter.page}&search=${filter.search}`;
    const response = await APIKit.get(url);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, userClients, [filter], requestId);
  }
}

export async function userClientsNew(body: ClientNewRequest) {
  const requestId = REQUEST_USERS_CLIENTS_NEW_ID;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.post(`${API_V1_USERS}/clients`, body);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, userClientsNew, [body], requestId);
  }
}

export async function userClientsStats(source: string | undefined, agent: boolean | undefined) {
  const requestId = REQUEST_USERS_CLIENTS_STATS_ID;
  try {
    store.dispatch(start(requestId));
    const baseUrl = `${API_V1_USERS}/clients/stats`;
    let url = baseUrl;
    if (source) url += (baseUrl === url ? "?" : "&") + `source=${source.toUpperCase()}`;
    if (agent) url += (baseUrl === url ? "?" : "&") + "agent=true";
    const response = await APIKit.get(url);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, userClientsStats, [source], requestId);
  }
}

export async function userClientsOne(id: string | number) {
  const requestId = REQUEST_USERS_CLIENTS_VIEW_ID;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.get(`${API_V1_USERS}/clients/${id}`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, userClientsOne, [id], requestId);
  }
}

export async function userClientUpdateProperty(
  id: number,
  propertyName: string,
  propertyValue: any,
) {
  const requestId = REQUEST_USERS_CLIENTS_UPDATE_PROPERTY_ID + id + propertyName;
  try {
    store.dispatch(start(requestId));
    const value = AppUtils.convertToAppropriateValue(propertyValue);
    const body = {
      name: propertyName,
      value: value,
    };
    const response = await APIKit.patch(`${API_V1_USERS}/clients/${id}`, body);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(
      err,
      userClientUpdateProperty,
      [id, propertyName, propertyValue],
      requestId,
    );
  }
}

export async function userClientDelete(id: string | number) {
  const requestId = REQUEST_USERS_CLIENTS_DELETE_ID;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.delete(`${API_V1_USERS}/clients/${id}`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, userClientDelete, [id], requestId);
  }
}

interface UserListFilterRequest extends ListFilterRequest {
  profileId?: number;
  type?: "CLIENT" | "EMPLOYEE";
  status?: UserStatus;
}
export async function usersAll(filter: UserListFilterRequest) {
  const requestId = REQUEST_USERS_LIST_ID;
  try {
    store.dispatch(start(requestId));
    let url = `${API_V1_USERS}?page=${filter.page}&search=${filter.search}`;
    if (filter.pageSize) url += `&pageSize=${filter.pageSize}`;
    if (filter.profileId) url += `&profileId=${filter.profileId}`;
    if (filter.type) url += `&type=${filter.type}`;
    if (filter.status) url += `&status=${filter.status}`;
    const response = await APIKit.get(url);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, usersAll, [filter], requestId);
  }
}

export async function usersCreate(data: UserRequest) {
  const requestId = REQUEST_USERS_CREATE_ID;
  try {
    store.dispatch(start(requestId));
    const body = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      countryCode: data.countryCode,
      phoneNumber: data.phoneNumber,
      profileId: data.profile.id,
      status: data.status,
    };
    const response = await APIKit.post(`${API_V1_USERS}`, body);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, usersCreate, [data], requestId);
  }
}

export async function usersUpdate(userId: number, data: UserRequest) {
  const requestId = REQUEST_USERS_UPDATE_ID;
  try {
    store.dispatch(start(requestId));
    const body = {
      firstName: data.firstName,
      lastName: data.lastName,
      countryCode: data.countryCode,
      phoneNumber: data.phoneNumber,
      profileId: data.profile.id,
      status: data.status,
    };
    const response = await APIKit.patch(`${API_V1_USERS}/${userId}`, body);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, usersUpdate, [userId, data], requestId);
  }
}

export async function userRecommendationsStats() {
  const requestId = REQUEST_USER_RECOMMENDATION_STATS_ID;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.get(`${API_V1_USER_RECOMMENDATIONS}/stats`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, userRecommendationsStats, [], requestId);
  }
}

interface UserRecommendationsListFilterRequest extends ListFilterRequest {
  crm: boolean;
}
export async function userRecommendationsAll(filter: UserRecommendationsListFilterRequest) {
  const requestId = REQUEST_USER_RECOMMENDATION_LIST_ID;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.get(
      `${API_V1_USER_RECOMMENDATIONS}?page=${filter.page}&search=${filter.search}&crm=${filter.crm}`,
    );
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, userRecommendationsAll, [filter], requestId);
  }
}

export async function userRecommendationTransferToCrm(id: number) {
  const requestId = REQUEST_USER_RECOMMENDATION_TRANSFER_TO_CRM_ID + id;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.post(`${API_V1_USER_RECOMMENDATIONS}/${id}/crm`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, userRecommendationTransferToCrm, [id], requestId);
  }
}

export async function changePassword(
  oldPassword: string,
  newPassword: string,
  confirmationPassword: string,
) {
  const requestId = REQUEST_USER_CHANGE_PASSWORD;
  try {
    store.dispatch(start(requestId));
    const body = {
      oldPassword: oldPassword,
      newPassword: newPassword,
      confirmationPassword: confirmationPassword,
    };
    const response = await APIKit.patch(`${API_V1_ME}/password-change`, body);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(
      err,
      changePassword,
      [oldPassword, newPassword, confirmationPassword],
      requestId,
    );
  }
}

export async function changeAvatar(file: FileWithPath) {
  const requestId = REQUEST_USER_CHANGE_AVATAR;
  try {
    store.dispatch(start(requestId));
    const formData = new FormData();
    formData.append("file", file);
    const response = await APIKit.post(`${API_V1_ME}/profile-image-change`, formData, {
      headers: {
        ...APIKit.defaults.headers["common"],
        "Accept-Language": APIKit.defaults.headers["Accept-Language"],
        "Content-Type": "multipart/form-data",
      },
    });
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, changeAvatar, [file], requestId);
  }
}

export async function userCommentsAll(id: number) {
  const requestId = REQUEST_USER_COMMENTS_LIST_ID + id;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.get(`${API_V1_USER_COMMENTS}?uid=${id}`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, userCommentsAll, [id], requestId);
  }
}

export async function userCommentsCreate(id: number, comment: string) {
  const requestId = REQUEST_USER_COMMENTS_CREATE_ID + id;
  try {
    store.dispatch(start(requestId));
    const body = {
      userId: id,
      comment: comment,
    };
    const response = await APIKit.post(`${API_V1_USER_COMMENTS}`, body);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, userCommentsCreate, [id, comment], requestId);
  }
}

export async function usersAgentsAll(filter: UserListFilterRequest) {
  const requestId = REQUEST_USER_AGENTS_LIST_ID;
  try {
    store.dispatch(start(requestId));
    let url = `${API_V1_USERS}/agents?page=${filter.page}&search=${filter.search}`;
    if (filter.pageSize) url += `&pageSize=${filter.pageSize}`;
    if (filter.status) url += `&status=${filter.status}`;
    const response = await APIKit.get(url);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, usersAgentsAll, [filter], requestId);
  }
}

export async function userAgentChange(clientId: number, agentId: number) {
  const requestId = REQUEST_USER_AGENT_CHANGE_ID + clientId;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.post(`${API_V1_USERS}/clients/${clientId}/agent/${agentId}`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, userAgentChange, [clientId, agentId], requestId);
  }
}

/**
 * Toggle-uje blacklist status klijenta
 */
export async function toggleClientBlacklist(clientId: number) {
  const requestId = REQUEST_USERS_CLIENTS_BLACKLIST_ID + clientId;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.patch(`${API_V1_USERS}/clients/blacklist/${clientId}`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, toggleClientBlacklist, [clientId], requestId);
  }
}

